import { useCallback, useContext, useEffect, useState } from 'react';
import { RewardsCoordinatorContext } from '../RewardsCoordinatorContext';
import type { HostModeEntryPhase } from '../HostModeEntryController';
import type { HostModeErrorCode } from '../hostModeErrors';
import type { CategorizedGates } from '../categorizeGates';

export interface HostModeEntryFlowState {
  phase: HostModeEntryPhase;
  categorized: CategorizedGates | null;
  error: string | null;
  failureCode: HostModeErrorCode | null;
  inFlight: boolean;
  start: () => void;
  retry: () => void;
  confirmRestart: () => void;
  dismiss: () => void;
}

export function useHostModeEntryFlow(): HostModeEntryFlowState {
  const coordinator = useContext(RewardsCoordinatorContext);
  if (!coordinator) {
    throw new Error('useHostModeEntryFlow must be used within a RewardsCoordinatorProvider');
  }

  const [ctrl] = useState(() => coordinator.createHostModeEntryFlow());
  const [, forceRender] = useState(0);

  useEffect(() => {
    const unsub = ctrl.subscribe(() => {
      forceRender((n) => n + 1);
    });

    return () => {
      unsub();
      ctrl.destroy();
    };
  }, [ctrl]);

  const start = useCallback(() => { ctrl.start(); }, [ctrl]);
  const retry = useCallback(() => { ctrl.retry(); }, [ctrl]);
  const confirmRestart = useCallback(() => { ctrl.confirmRestart(); }, [ctrl]);
  const dismiss = useCallback(() => { ctrl.dismiss(); }, [ctrl]);

  return {
    phase: ctrl.getPhase(),
    categorized: ctrl.getCategorized(),
    error: ctrl.getError(),
    failureCode: ctrl.getFailureCode(),
    inFlight: ctrl.isInFlight(),
    start,
    retry,
    confirmRestart,
    dismiss,
  };
}
